/**
 * NuevaLecturaDialog — Formulario móvil-first para registrar lectura AM/PM
 * del nivel del tanque. Foto del medidor → OCR → editable → guardar.
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, MenuItem, Box, Typography, Grid, CircularProgress,
  Alert, Chip, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import Brightness3Icon from '@mui/icons-material/Brightness3';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

function getAuthHeaders() {
  const t = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}

const hoyStr = () => new Date().toISOString().slice(0, 10);
const inferMomento = () => new Date().getHours() < 14 ? 'am' : 'pm';

export default function NuevaLecturaDialog({ open, embarcaciones, empresaId, onClose, onSuccess, onError }) {
  const [form, setForm] = useState({
    embarcacion_id: '',
    fecha: hoyStr(),
    momento: inferMomento(),
    nivel_galones: '',
    nivel_pct: '',
    foto_medidor_url: '',
    notas: '',
  });
  const [subiendo, setSubiendo] = useState(false);
  const [analizando, setAnalizando] = useState(false);
  const [ocr, setOcr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fotoNombre, setFotoNombre] = useState('');

  useEffect(() => {
    if (open) {
      const preselect = embarcaciones && embarcaciones.length === 1
        ? { embarcacion_id: embarcaciones[0].id } : {};
      setForm({
        embarcacion_id: '',
        fecha: hoyStr(),
        momento: inferMomento(),
        nivel_galones: '',
        nivel_pct: '',
        foto_medidor_url: '',
        notas: '',
        ...preselect,
      });
      setOcr(null);
      setFotoNombre('');
    }
  }, [open, embarcaciones]);

  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const subirFoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append('archivo', file);
      const t = localStorage.getItem('token');
      const r = await fetch(`${API_BASE}/turismo/soportes/upload`, {
        method: 'POST',
        headers: t ? { Authorization: `Bearer ${t}` } : {},
        body: fd,
      });
      if (!r.ok) throw new Error('Error subiendo');
      const d = await r.json();
      setForm(prev => ({ ...prev, foto_medidor_url: d.url }));
      setFotoNombre(file.name);
      analizar(d.url);
    } catch (err) {
      onError?.('Error al subir la foto');
    } finally { setSubiendo(false); }
  };

  const analizar = async (url) => {
    setAnalizando(true);
    try {
      const r = await fetch(`${API_BASE}/turismo/combustible/analizar-medidor`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ url, empresa_id: empresaId }),
      });
      const d = await r.json();
      setOcr(d);
      if (d.ok) {
        setForm(prev => ({
          ...prev,
          nivel_galones: prev.nivel_galones || (d.nivel_galones != null ? String(d.nivel_galones) : ''),
          nivel_pct: prev.nivel_pct || (d.nivel_pct != null ? String(d.nivel_pct) : ''),
        }));
      }
    } catch (e) {
      setOcr({ ok: false, error: 'No se pudo analizar' });
    } finally { setAnalizando(false); }
  };

  const puedeGuardar = form.embarcacion_id
    && form.fecha
    && form.momento
    && (Number(form.nivel_galones) >= 0 || Number(form.nivel_pct) >= 0)
    && (form.nivel_galones || form.nivel_pct);

  const guardar = async () => {
    if (!puedeGuardar || saving) return;
    setSaving(true);
    try {
      const body = {
        empresa_id: empresaId,
        embarcacion_id: Number(form.embarcacion_id),
        fecha: form.fecha,
        momento: form.momento,
        nivel_galones: form.nivel_galones ? Number(form.nivel_galones) : null,
        nivel_pct: form.nivel_pct ? Number(form.nivel_pct) : null,
        foto_medidor_url: form.foto_medidor_url || null,
        ocr_resultado: ocr || {},
        notas: form.notas.trim() || null,
      };
      const r = await fetch(`${API_BASE}/turismo/combustible/lecturas`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) {
        onError?.(d.detail?.message || d.detail || 'Error al guardar');
        return;
      }
      onSuccess?.(`Lectura ${form.momento.toUpperCase()} guardada: ${form.nivel_galones || form.nivel_pct + '%'} gal`);
    } catch (e) {
      onError?.('Error de red');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      fullScreen={typeof window !== 'undefined' && window.innerWidth < 600}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
        <LocalGasStationIcon sx={{ color: '#0ea5e9' }} />
        Nueva lectura de tanque
      </DialogTitle>
      <DialogContent>
        {/* Embarcación + momento */}
        <TextField fullWidth select size="small" label="Embarcación *"
          value={form.embarcacion_id} onChange={set('embarcacion_id')}
          sx={{ mt: 1, mb: 2 }}
        >
          {embarcaciones.length === 0 && (
            <MenuItem value="" disabled>No tienes embarcaciones asignadas</MenuItem>
          )}
          {embarcaciones.map(e => (
            <MenuItem key={e.id} value={e.id}>{e.nombre}</MenuItem>
          ))}
        </TextField>

        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={7}>
            <TextField fullWidth size="small" type="date" label="Fecha"
              InputLabelProps={{ shrink: true }}
              value={form.fecha} onChange={set('fecha')}
            />
          </Grid>
          <Grid item xs={5}>
            <ToggleButtonGroup
              value={form.momento} exclusive
              onChange={(_, v) => v && setForm(p => ({ ...p, momento: v }))}
              size="small" fullWidth
            >
              <ToggleButton value="am" sx={{ fontWeight: 700, textTransform: 'none' }}>
                <WbSunnyIcon sx={{ fontSize: 16, mr: 0.5 }} /> AM
              </ToggleButton>
              <ToggleButton value="pm" sx={{ fontWeight: 700, textTransform: 'none' }}>
                <Brightness3Icon sx={{ fontSize: 16, mr: 0.5 }} /> PM
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        </Grid>

        {/* Foto medidor */}
        <Box sx={{ p: 1.5, borderRadius: 2, border: '1px dashed #cbd5e1', bgcolor: '#eff6ff', mb: 2 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0ea5e9', mb: 1, textTransform: 'uppercase' }}>
            Foto del medidor
          </Typography>
          {form.foto_medidor_url ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon sx={{ color: '#16a34a' }} />
              <Typography sx={{ fontSize: 12, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {fotoNombre}
              </Typography>
              <Button size="small"
                onClick={() => {
                  setForm(p => ({ ...p, foto_medidor_url: '' })); setFotoNombre(''); setOcr(null);
                }}
                sx={{ textTransform: 'none', color: '#dc2626', fontSize: 11 }}
              >
                Quitar
              </Button>
            </Box>
          ) : (
            <Button component="label" fullWidth variant="contained" size="large"
              disabled={subiendo} startIcon={<CameraAltIcon />}
              sx={{
                textTransform: 'none', fontWeight: 700,
                background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                py: 1.5,
              }}
            >
              {subiendo ? 'Subiendo…' : 'Tomar foto del medidor'}
              <input hidden type="file" accept="image/*" capture="environment" onChange={subirFoto} />
            </Button>
          )}
          {analizando && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={14} sx={{ color: '#0ea5e9' }} />
              <Typography sx={{ fontSize: 11, color: '#0ea5e9' }}>
                Claude Vision leyendo el medidor…
              </Typography>
            </Box>
          )}
          {ocr && ocr.ok && (
            <Box sx={{ mt: 1 }}>
              <Chip label={`✨ IA detectó · confianza ${Math.round(ocr.confianza * 100)}%`} size="small"
                sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 700, fontSize: 10 }}
              />
              {ocr.posicion_aguja && (
                <Typography sx={{ fontSize: 11, color: '#0f172a', mt: 0.5 }}>
                  Aguja en: <strong>{ocr.posicion_aguja}</strong>
                </Typography>
              )}
            </Box>
          )}
          {ocr && !ocr.ok && (
            <Alert severity="warning" sx={{ mt: 1, fontSize: 11 }}>
              IA no pudo leer bien. Ingresa el nivel manualmente.
            </Alert>
          )}
        </Box>

        {/* Nivel */}
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mb: 1, textTransform: 'uppercase' }}>
          Nivel del tanque *
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={6}>
            <TextField fullWidth size="small" type="number" label="Galones"
              value={form.nivel_galones} onChange={set('nivel_galones')}
              inputProps={{ step: 0.1, min: 0 }}
              helperText="Si el medidor los muestra directo"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth size="small" type="number" label="% (opcional)"
              value={form.nivel_pct} onChange={set('nivel_pct')}
              inputProps={{ step: 1, min: 0, max: 100 }}
              helperText="Si sólo tienes % o aguja"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" multiline rows={2} label="Notas"
              value={form.notas} onChange={set('notas')}
              placeholder="Ej: aguja entre 1/2 y 3/4"
            />
          </Grid>
        </Grid>

        <Alert severity="info" sx={{ mt: 2, fontSize: 11 }}>
          Si ya existe una lectura para esta embarcación en {form.momento.toUpperCase()} de esa fecha,
          se reemplazará por esta nueva (upsert).
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancelar</Button>
        <Button variant="contained" onClick={guardar} disabled={!puedeGuardar || saving}
          sx={{
            textTransform: 'none', fontWeight: 700,
            background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
          }}
        >
          {saving ? 'Guardando…' : 'Guardar lectura'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
